import { useEffect, useState } from 'react';
import type { Asset } from '@vidmakr/bindings/types';
import {
  CONTAINERS,
  DEFAULT_POOL,
  ENCODE_FPS,
  SHARED_GPU_POOL,
  codecLabel,
  containerSpec,
  encodeHasVideo,
  encodePreset,
  estimateEncodeBytes,
  isGpuPool,
  isMasterEncode,
  nvencSupports,
  orgGpuPool,
  presetEncode,
  presetSize,
  renderMinutesFor,
  validateEncode,
  type EncodeSettings,
} from '@vidmakr/shared';
import { Button, Field, Input, Modal, Select } from '@/components/ui';
import { EncodeSettingsEditor, PresetActions, PresetPicker, findPreset, useUserPresets } from '@/components/EncodeSettings';
import { formatMinutes, useOrgPlan } from '@/lib/billing';
import { formatBytes } from '@/lib/format';
import { reducers } from '@/lib/stdb';
import { attempt } from '@/lib/toast';

/** Formats a converted file can have: playable media, not image sequences. */
const CONVERTIBLE = CONTAINERS.filter((c) => c.convertible).map((c) => c.id);

/**
 * Convert media to other formats: pick a preset (or set everything under
 * Advanced) and each selected file is transcoded on the workers into a new
 * file next to it, "<name> (<preset>).<ext>". Progress shows under Exports.
 */
export function ConvertDialog({ assets, onClose }: { assets: Asset[] | null; onClose: () => void }) {
  const userPresets = useUserPresets();
  const [presetId, setPresetId] = useState('prores_422_hq');
  const [encode, setEncodeState] = useState<EncodeSettings>(() => presetEncode(encodePreset('prores_422_hq')!));
  const [size, setSize] = useState<'source' | 'preset' | 'custom'>('source');
  const [custom, setCustom] = useState({ w: 1920, h: 1080 });
  const [fps, setFps] = useState(0);
  const [label, setLabel] = useState('ProRes 422 HQ');
  const [pool, setPool] = useState(DEFAULT_POOL);
  const [busy, setBusy] = useState(false);
  const list = assets ?? [];
  const orgId = list[0]?.orgId;
  const billing = useOrgPlan(orgId);
  const planSpec = billing?.enabled ? billing.limits.plan : null;
  const masterLocked = !!planSpec && !planSpec.masterFormats;
  const anyVideo = list.some((a) => a.hasVideo);
  const preset = findPreset(presetId, userPresets.presets);

  // Audio-only files start on an audio preset.
  useEffect(() => {
    if (assets?.length && !assets.some((a) => a.hasVideo)) applyPreset('audio_wav48');
  }, [assets]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (isGpuPool(pool) && !nvencSupports(encode)) setPool(DEFAULT_POOL);
  }, [encode, pool]);

  if (!assets?.length) return null;

  function applyPreset(id: string) {
    const p = findPreset(id, userPresets.presets);
    if (!p) return;
    setPresetId(id);
    setEncodeState(p.encode);
    setSize(p.width || p.shortSide ? 'preset' : 'source');
    setFps(p.fps ?? 0);
    setLabel(p.label.replace(/^Broadcast · /, ''));
  }
  const setEncode = (e: EncodeSettings) => {
    setEncodeState(e);
    if (presetId !== 'custom') setLabel(codecLabel(e));
    setPresetId('custom');
  };
  const video = encodeHasVideo(encode);
  /** Output size for one file (0×0 = its own). */
  const sizeFor = (a: Asset) =>
    !video || size === 'source' || !preset && size === 'preset'
      ? { width: 0, height: 0 }
      : size === 'custom'
        ? { width: custom.w, height: custom.h }
        : presetSize(preset!, a.width, a.height);
  const usable = list.filter((a) => (video ? a.hasVideo : a.hasAudio));
  const problems = usable.map((a) => {
    const s = sizeFor(a);
    const w = s.width || Math.floor(a.width / 2) * 2;
    const h = s.height || Math.floor(a.height / 2) * 2;
    return validateEncode(encode, { width: video ? w : 0, height: video ? h : 0, fps: fps || a.fps, gpu: isGpuPool(pool) });
  });
  const problem = problems.find(Boolean) ?? (usable.length ? null : video ? 'None of these files has a picture' : 'None of these files has sound');
  const estimate = usable.reduce((n, a) => {
    const s = sizeFor(a);
    return n + estimateEncodeBytes(encode, { width: s.width || a.width, height: s.height || a.height, fps: fps || a.fps || 30, durationUs: a.durationUs });
  }, 0);
  const minutes =
    pool === orgGpuPool(orgId ?? '') ? 0 : usable.reduce((n, a) => n + renderMinutesFor({ width: sizeFor(a).width || a.width || 1280, height: sizeFor(a).height || a.height || 720, encode }, a.durationUs), 0);
  const outOfMinutes = !!billing?.enabled && minutes > billing.renderLeft + 1e-9;
  const locked = masterLocked && isMasterEncode(encode);

  const start = async () => {
    setBusy(true);
    // A preset size keeps each file's aspect ratio: convert those one by one.
    const groups = size === 'preset' && video ? usable.map((a) => [a]) : [usable];
    const ok = await attempt(async () => {
      for (const g of groups) {
        const s = sizeFor(g[0]);
        await reducers().requestConvert({ assetIds: g.map((a) => a.id), encode: JSON.stringify(encode), width: s.width, height: s.height, fps, pool, label, startUs: 0, endUs: 0, name: '' });
      }
    }, `${usable.length === 1 ? 'Conversion' : `${usable.length} conversions`} started · follow them under Exports`);
    setBusy(false);
    if (ok) onClose();
  };

  return (
    <Modal open onClose={onClose} title={list.length === 1 ? `Convert “${list[0].name}”` : `Convert ${list.length} files`} width={620}>
      <div className="grid grid-cols-2 gap-4" data-convert-dialog>
        <div className="col-span-2">
          <Field label="Preset" hint={preset ? (encodePreset(presetId)?.description ?? 'Saved preset') : 'Custom settings'}>
            <PresetPicker
              value={presetId}
              onChange={applyPreset}
              user={userPresets.presets}
              allow={(e) => !!containerSpec(e.container)?.convertible && (anyVideo || !encodeHasVideo(e))}
              locked={(e) => masterLocked && isMasterEncode(e)}
            />
          </Field>
        </div>
        {video ? (
          <>
            <Field label="Frame size">
              <Select className="w-full" value={size} onChange={(e) => setSize(e.target.value as typeof size)} data-convert-size>
                <option value="source">Match source</option>
                {preset && (preset.width || preset.shortSide) ? (
                  <option value="preset">{preset.width ? `${preset.width}×${preset.height}` : `${preset.shortSide}p (keep aspect ratio)`}</option>
                ) : null}
                <option value="custom">Custom…</option>
              </Select>
            </Field>
            <Field label="Frame rate">
              <Select className="w-full" value={fps} onChange={(e) => setFps(Number(e.target.value))}>
                <option value={0}>Match source</option>
                {ENCODE_FPS.map((f) => (
                  <option key={f} value={f}>
                    {f} fps
                  </option>
                ))}
              </Select>
            </Field>
            {size === 'custom' ? (
              <div className="col-span-2 flex items-end gap-2">
                <Field label="Width">
                  <Input type="number" min={16} max={7680} step={2} value={custom.w} onChange={(e) => setCustom({ ...custom, w: Math.round(Number(e.target.value) || 0) })} />
                </Field>
                <span className="pb-2 text-muted">×</span>
                <Field label="Height">
                  <Input type="number" min={16} max={7680} step={2} value={custom.h} onChange={(e) => setCustom({ ...custom, h: Math.round(Number(e.target.value) || 0) })} />
                </Field>
              </div>
            ) : null}
          </>
        ) : null}
        <Field label="Name ends with" hint={`“${list[0].name.replace(/\.[A-Za-z0-9]{1,8}$/, '')} (${label || codecLabel(encode)})”`}>
          <Input value={label} maxLength={60} onChange={(e) => setLabel(e.target.value)} data-convert-label />
        </Field>
        <Field label="Convert on">
          <Select className="w-full" value={pool} onChange={(e) => setPool(e.target.value)}>
            <option value={DEFAULT_POOL}>Cloud (CPU)</option>
            <option value={orgGpuPool(orgId ?? '')} disabled={!nvencSupports(encode) || (!!planSpec && !planSpec.ownWorkers)}>
              Our GPU workers{nvencSupports(encode) ? '' : ' (H.264, H.265, AV1 only)'}
            </option>
            <option value={SHARED_GPU_POOL} disabled={!nvencSupports(encode) || (!!planSpec && !planSpec.gpu)}>
              Shared GPU{nvencSupports(encode) ? '' : ' (H.264, H.265, AV1 only)'}
            </option>
          </Select>
        </Field>
      </div>
      <div className="mt-3">
        <EncodeSettingsEditor value={encode} onChange={setEncode} containers={CONVERTIBLE} masterLocked={masterLocked} matchSource />
      </div>
      {problem ? (
        <p className="mt-3 text-xs text-danger" data-encode-error>
          {problem}
        </p>
      ) : usable.length < list.length ? (
        <p className="mt-3 text-xs text-warn">
          {list.length - usable.length} of the files {video ? 'have no picture' : 'have no sound'} and will be skipped.
        </p>
      ) : null}
      {locked ? <p className="mt-3 text-xs text-warn">This codec is part of the Pro plan.</p> : null}
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <PresetActions
          presetId={presetId}
          onSave={(name) => setPresetId(userPresets.save({ label: name, encode, ...(size === 'custom' ? { width: custom.w, height: custom.h } : {}), ...(fps ? { fps } : {}) }))}
          onDelete={(id) => {
            userPresets.remove(id);
            setPresetId('custom');
          }}
        />
        <div className="flex-1" />
        <span className="text-xs text-muted" data-size-estimate>
          ≈ {formatBytes(estimate)}
        </span>
        {billing?.enabled ? (
          <span className={outOfMinutes ? 'text-xs text-danger' : 'text-xs text-muted'}>
            {formatMinutes(minutes)} render min · {formatMinutes(billing.renderLeft)} left
          </span>
        ) : null}
        <Button variant="primary" loading={busy} disabled={!!problem || locked || outOfMinutes || !usable.length} onClick={start} data-start-convert>
          Convert {usable.length > 1 ? `${usable.length} files` : ''}
        </Button>
      </div>
    </Modal>
  );
}
